"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

const DesignationContext = createContext();

export const useDesignation = () => useContext(DesignationContext);

export const DesignationProvider = ({ children }) => {
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Get token from localStorage
  const getToken = () => localStorage.getItem("token");

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // 🔹 Fetch all designations
  const fetchDesignations = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getToken();

      if (!token) return;

      const res = await fetch(`${BASE_URL}/designations`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        setError("Failed to fetch designations");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200 || res.ok) {
        setDesignations(data.data || []);
        setError(null);
      } else {
        setError(data.message || "Failed to fetch designations");
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Error fetching designations");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Create new designation
  const createDesignation = async (formData) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/designations`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to create designation",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      await fetchDesignations();

      toast.success(data.message || "Designation created successfully");

      return {
        success: true,
        message: data.message,
        data,
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: err.message || "Error creating designation",
      };
    }
  };

  // 🔹 Update designation
  const updateDesignation = async (id, formData) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/designations/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to update designation",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      await fetchDesignations();

      toast.success(data.message || "Designation updated successfully");

      return {
        success: true,
        message: data.message,
        data,
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: err.message || "Error updating designation",
      };
    }
  };

  // 🔹 Delete designation
  const deleteDesignation = async (id, auditId) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/designations/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ delete_audit_id: auditId }),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to delete designation",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      await fetchDesignations();

      toast.success(data.message || "Designation deleted successfully");

      return {
        success: true,
        message: data.message,
        data,
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: err.message || "Error deleting designation",
      };
    }
  };

  useEffect(() => {
    fetchDesignations();
  }, []);

  return (
    <DesignationContext.Provider
      value={{
        designations,
        loading,
        error,
        fetchDesignations,
        createDesignation,
        updateDesignation,
        deleteDesignation,
      }}
    >
      {children}
    </DesignationContext.Provider>
  );
};